import { useEffect, useRef, useState } from "react";
import { csvFormat } from "d3-dsv";
import { Download } from "lucide-react";
import { toast } from "react-toastify";
import api from "../helpers/axios";
import ModalPortal from "./ModalPortal";

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "";

const buildCsv = (tickets) =>
  csvFormat(
    tickets.map((ticket) => ({
      "Ticket Id": ticket.reference_key ?? "",
      "Title": ticket.title ?? "",
      "Description": ticket.description ?? "",
      "User Email": ticket.user_email ?? "",
      "Submitted On": formatDate(ticket.created_at),
      "Updated On": formatDate(ticket.updated_at),
      "Priority": ticket.priority ?? "",
      "Status": ticket.status ?? "",
    }))
  );

const downloadCsv = (csv) => {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `support-tickets-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

// Blocking popup that exports every ticket matching the current filters.
// The backend returns all matching tickets in one unpaginated response
// (export=true), which gets turned into a CSV download. The overlay ignores
// outside clicks so the export can't be interrupted by accident — only the
// Cancel button stops it.
const ExportTicketsModal = ({ open, filters, onClose }) => {
  const [failed, setFailed] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    cancelledRef.current = false;
    setFailed(false);

    const runExport = async () => {
      try {
        const params = new URLSearchParams({ export: "true" });

        // Multi-valued filters: repeat the param once per selected value
        filters.statuses.forEach((status) => params.append("status", status));
        filters.priorities.forEach((priority) => params.append("priority", priority));

        if (filters.search) params.append("search", filters.search);
        if (filters.facilityId) params.append("facility_id", filters.facilityId);

        const res = await api.get(`/help-center/all-requests?${params.toString()}`);
        if (cancelledRef.current) return;

        const tickets = res.data?.data ?? [];
        downloadCsv(buildCsv(tickets));
        toast.success(
          `Exported ${res.data?.export?.returned_count ?? tickets.length} tickets`
        );
        onClose();
      } catch (err) {
        console.error("Failed to export tickets:", err);
        if (!cancelledRef.current) setFailed(true);
      }
    };

    runExport();
  }, [open, retryCount]);

  // The in-app overlay can't stop a tab close or refresh, so while the
  // export runs the browser's own "leave site?" warning covers that.
  useEffect(() => {
    if (!open) return;

    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open]);

  if (!open) return null;

  const handleCancel = () => {
    cancelledRef.current = true;
    onClose();
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-40 p-6">
        <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
          {failed ? (
            <div className="text-center">
              <p className="text-sm font-semibold text-gray-700">
                Couldn't export tickets.
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Something went wrong while preparing your file.
              </p>
              <div className="mt-5 flex justify-center gap-3">
                <button
                  onClick={() => setRetryCount((count) => count + 1)}
                  className="rounded-full bg-[#2B3674] px-5 py-2 text-sm font-medium text-white hover:bg-[#232d61] transition"
                >
                  Try Again
                </button>
                <button
                  onClick={handleCancel}
                  className="rounded-full border border-gray-200 px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
                >
                  Close
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-purple-50">
                <Download size={20} className="animate-bounce text-accent-purple-600" />
              </div>
              <p className="mt-4 text-sm font-semibold text-gray-700">
                Exporting tickets…
              </p>
              <p className="mt-1 text-sm text-gray-500">
                Preparing your file for download
              </p>
              <button
                onClick={handleCancel}
                className="mt-5 rounded-full border border-gray-200 px-5 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <p className="mt-4 text-xs text-gray-400">
                Please don't close or refresh this window while we prepare your file.
              </p>
            </div>
          )}
        </div>
      </div>
    </ModalPortal>
  );
};

export default ExportTicketsModal;
